<template>
  <div :class='$style.team_login_wrap'>
    <div :class='$style.team_login'>
      <h1 :class='$style.title'>ログイン(組織ログイン)</h1>

      <!-- 組織ログイン利用可否確認中の表示 -->
      <div v-if="loading" :class="$style.loading_wrap">
        <base-icon name="spinner" size="32px" spin />
        <p :class="$style.loading_text">確認中...</p>
      </div>

      <!-- 組織ログインが利用不可の場合 -->
      <div v-else-if="!isTeamLoginAvailable" :class="$style.error_wrap">
        <base-message type="error" width="auto">
          <template>
            この組織ではログイン機能をご利用いただけません。
          </template>
        </base-message>
      </div>

      <!-- 組織ログインが利用可能な場合 -->
      <div v-else>
      <p :class='$style.note'>ご自身のユーザーID、パスワードを入力してください</p>

      <div :class='$style.input_group'>
        <base-input
          name="employee_no"
          look="border"
          v-model="employeeNo"
          maxlength="50"
          :error="v$.employeeNo.$invalid && v$.employeeNo.$dirty ? v$.employeeNo.$errors[0].$message : ''">
          <template v-slot:prepend>
            <span :class='$style.input_prepend'>ユーザーID</span>
          </template>
          <template v-slot:append>
            <base-icon v-if="!v$.employeeNo.$invalid && v$.employeeNo.$dirty" name="check" color="green" />
          </template>
        </base-input>

        <base-input
          name="password"
          :type="showPassword ? 'text' : 'password'"
          look="border"
          v-model="password"
          maxlength="256"
          :error="v$.password.$invalid && v$.password.$dirty ? v$.password.$errors[0].$message : ''">
          <template v-slot:prepend>
            <span :class='$style.input_prepend'>パスワード</span>
          </template>
          <template v-slot:append>
            <div :class='$style.input_prepend_contact' @click="toggleShowPassword">
              <base-icon :name="showPassword ? 'eye' : 'eye-slash'" color="black" :class="$style.cursor_pointer" />
            </div>
          </template>
        </base-input>
      </div>

      <div :class='$style.next_button_wrap'>
        <p v-if="loginError" :class="$style.error_txt">{{ loginError }}</p>
        <base-button
          display='inline-block'
          width='100%'
          height='50px'
          @click="login"
          :disabled="loginSending">
          ログイン
        </base-button>
      </div>

      <div :class='$style.link_wrap'>
        <a href="/open/password_forget" :class="$style.link">ID・パスワードを忘れた方はこちら</a>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
import BaseInput from '@/components/base/BaseInput/BaseInput'
import BaseButton from '@/components/base/BaseButton/BaseButton'
import BaseIcon from '@/components/base/BaseIcon/BaseIcon'
import BaseMessage from '@/components/base/BaseMessage/BaseMessage'
import useVuelidate from "@vuelidate/core"
import { required, helpers } from "@vuelidate/validators"

export default {
  name: 'TeamLogin',
  components: {
    BaseInput,
    BaseButton,
    BaseIcon,
    BaseMessage
  },
  data() {
    return {
      teamLoginKey: '',
      employeeNo: '',
      password: '',
      showPassword: false,
      loading: true,
      isTeamLoginAvailable: false,
      loginSending: false,
      loginError: '',
    }
  },
  setup() {
    return { v$: useVuelidate() }
  },
  validations() {
    return {
      employeeNo: {
        required: helpers.withMessage('組織内識別子の入力は必須です。', required),
        $autoDirty: true
      },
      password: {
        required: helpers.withMessage('パスワードの入力は必須です。', required),
        $autoDirty: true
      },
    }
  },
  async created() {
    // URLからteam_login_keyを取得
    this.teamLoginKey = this.$route.query.team_login_key

    if (!this.teamLoginKey) {
      this.loading = false
      this.loginError = '組織ログインキーが指定されていません。'
      return
    }

    // 組織ログイン利用可否確認API呼び出し
    await this.checkTeamLoginAvailability()

    // スマホアプリがインストールされている場合は自動的に起動
    if (this.isTeamLoginAvailable) {
      this.tryLaunchMobileApp()
    }
  },
  methods: {
    toggleShowPassword() {
      this.showPassword = !this.showPassword
    },

    tryLaunchMobileApp() {
      // カスタムURLスキーム方式でアプリ起動を試みる（サイレント）
      const deepLinkUrl = this.getDeepLinkUrl()

      if (!deepLinkUrl) return

      // iframeを使ってサイレントにアプリ起動を試みる
      const iframe = document.createElement('iframe')
      iframe.style.display = 'none'
      iframe.src = deepLinkUrl

      document.body.appendChild(iframe)

      // 短時間後にiframeを削除（アプリが起動しなくてもエラーにならない）
      setTimeout(() => {
        document.body.removeChild(iframe)
      }, 1000)

      // アプリが起動しなかった場合はWeb画面をそのまま表示
      // （何もしない = ログインフォームが表示されたまま）
    },

    getDeepLinkUrl() {
      // カスタムURLスキーム方式（例: kumiaiapp://）
      // アプリ側で設定されたカスタムスキームを使用
      const customScheme = process.env.VUE_APP_CUSTOM_SCHEME || 'kumiaiapp'

      // ディープリンクURL構築
      let url = `${customScheme}://open/team/login?team_login_key=${encodeURIComponent(this.teamLoginKey)}`

      // redirectパラメータがあれば追加
      if (this.$route.query.redirect) {
        url += `&redirect=${encodeURIComponent(this.$route.query.redirect)}`
      }

      return url
    },

    async checkTeamLoginAvailability() {
      try {
        const response = await this.$axios.get(
          `/api/${this.$constants.API_VERSION}/team/login/check`,
          {
            params: {
              team_login_key: this.teamLoginKey
            }
          }
        )

        if (response.data && response.data.status === 'success') {
          this.isTeamLoginAvailable = response.data.value.team_login_flg === 1
        } else {
          this.isTeamLoginAvailable = false
        }
      } catch (error) {
        this.isTeamLoginAvailable = false
        if (error.response && error.response.data && error.response.data.value) {
          const errors = error.response.data.value.errors
          if (errors && errors.length > 0) {
            this.loginError = errors[0].message
          }
        }
      } finally {
        this.loading = false
      }
    },

    async login() {
      const result = await this.v$.$validate()

      if (!result) {
        return
      }

      if (this.loginSending) {
        return
      }

      // 連投させないようにログイン中フラグを立てる
      this.loginSending = true
      this.loginError = ''

      const postData = {
        team_login_key: this.teamLoginKey,
        employee_no: this.employeeNo,
        password: this.password
      }

      try {
        const response = await this.$axios.post(
          `/api/${this.$constants.API_VERSION}/team/login`,
          postData
        )

        const authInfo = response.data.value

        // 二要素認証が必要な場合
        if (authInfo.is_tfa === 1) {
          const uuid = authInfo.uuid
          const redirect = this.$route.query.redirect
          this.$router.push({
            path: '/open/tfa',
            query: { uuid: uuid, redirect: redirect }
          })
          return
        }

        // 認証情報をCookieに保存
        this.$cookies.set('auth_info', {
          'tel_or_email': authInfo.tel_or_email,
          'access_token': authInfo.access_token,
          'expired_at': this.$dayjs().add(authInfo.expires_in, 's').format(),
          'refresh_token': authInfo.refresh_token
        })

        // Vuexストアに情報を保存
        this.$store.commit('auth/setTelOrEmail', authInfo.tel_or_email)
        this.$store.commit('auth/setJuid', authInfo.juid)
        this.$store.commit('auth/setUserId', authInfo.user_id)

        // スマホアプリの起動を試みる
        this.tryLaunchMobileApp()

        // 状態更新を確実にするため次のtickまで待機
        await this.$nextTick()

        // ログイン後の遷移処理
        await this.switchNextRouting()
      } catch (error) {
        if (error.response && error.response.data && error.response.data.value) {
          const errors = error.response.data.value.errors
          if (errors && errors.length > 0) {
            this.loginError = errors[0].message
          } else {
            this.loginError = 'ログインに失敗しました。入力項目を見直してください。'
          }
        } else {
          this.loginError = 'ログインに失敗しました。入力項目を見直してください。'
        }
      } finally {
        this.loginSending = false
      }
    },

    switchNextRouting: async function () {
      // user_idがない場合は強制的にプロフィール登録へ
      if (!this.$store.state.auth.user_id) {
        await this.$router.push('/open/profile_edit')
        return
      }

      // redirectパラメータがある場合はそちらへ
      const route = this.$route.query.redirect
      if (route) {
        await this.$router.push(route)
      } else {
        await this.$router.push('/home/home')
      }
    }
  }
}
</script>

<style lang='scss' module>
.team_login_wrap {
  min-height: 100vh;
  background: #FFFBE4;
  display: flex;
  align-items: center;
  justify-content: center;
}

.team_login {
  display: flex;
  flex-direction: column;
  width: 375px;
  margin: 0 auto;
  padding: 42px 32px 30px 32px;
  box-sizing: border-box;
  text-align: left;

  @include mobile() {
    width: 375px;
    padding: 42px 32px 30px 32px;
  }
}

.title {
  margin-top: 0;
  font-size: 24px;
  font-weight: normal;
  line-height: 36px;
  margin-bottom: 50px;

  @include mobile() {
    font-size: 24px;
    margin-bottom: 50px;
  }
}

.loading_wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}

.loading_text {
  margin-top: 16px;
  font-size: 14px;
  color: $fontBlack;
}

.error_wrap {
  margin-top: 20px;
}

.note {
  font-size: 13px;
  color: $fontBlack;
  margin-top: 0;
  margin-bottom: 60px;
}

.input_group {
  & > * {
    &:nth-child(1) {
      margin-bottom: 54px;
    }
  }

  margin-bottom: 30px;
}

.input_prepend {
  font-size: 11px;
  color: $fontBlack !important;

  &_contact {
    margin-right: 0px;
  }
}

.cursor_pointer {
  cursor: pointer;
}

.next_button_wrap {
  margin-top: 50px;
}

$textSize_Error: 13px;
$textSpecing_Error: 4px;
$textColor_Error: #ff0000;

.error_txt {
  font-size: $textSize_Error;
  margin: $textSpecing_Error 0 0 0;
  color: $textColor_Error;
}

.link_wrap {
  text-align: center;
}

.link {
  display: block;
  font-size: 13px;
  line-height: 30px;
  color: $fontBlack;
  margin-top: 0;
  margin-bottom: 0px;
}
</style>
